import {
  TRUSTED_SENDER_STATUS_DELIVER_ERROR,
  TRUSTED_SENDER_STATUS_DELIVER_ISP,
  TRUSTED_SENDER_STATUS_NOT_CONFIRMED
} from 'Api/beSendApi'
import { generateCancelToken } from 'Helpers/apiWrapper'
import debounce from 'lodash/debounce'
import { useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useRecoilState, useRecoilCallback } from 'recoil'

import {
  onChangeValueCallback,
  registerSenderCallback,
  statusPollingCallback,
  dismissWarningCallback
} from './callbacks'
import { trustedEmailSenderState } from './state'

const POLLING_INTERVAL = 5000

const useTrustedEmailSender = (
  itemKey,
  required = false,
  initialValue = '',
  onRegisterSuccess,
  onRegisterError
) => {
  const intl = useIntl()

  const [
    {
      value,
      variant,
      invalidRequired,
      invalidFormat,
      status,
      pollingError,
      pollingIteration
    },
    setState
  ] = useRecoilState(trustedEmailSenderState(itemKey))

  const cancelToken = useRef(null)
  const pollingCancelToken = useRef(null)
  const pollingTimer = useRef(null)

  const onChangeValue = useRecoilCallback(onChangeValueCallback, [])
  const registerSender = useRecoilCallback(registerSenderCallback, [])
  const statusPolling = useRecoilCallback(statusPollingCallback, [])
  const dismissWarning = useRecoilCallback(dismissWarningCallback, [])

  const handleChange = ({ target: { value } }) => {
    setState((prev) => ({ ...prev, value: value.trim() }))
  }

  const debouncedOnChangeValue = useMemo(() => debounce(onChangeValue, 250), [])

  const handleVerifyClick = () => {
    if (pollingCancelToken.current) {
      pollingCancelToken.current.cancel('oops verify')
    }
    const t = generateCancelToken()
    pollingCancelToken.current = t

    registerSender(itemKey, t.token, onRegisterSuccess, onRegisterError)
  }

  const handleDismissWarningClick = () => {
    dismissWarning(itemKey)
  }

  //  on mount
  useEffect(() => {
    setState((prev) => ({ ...prev, value: initialValue, required }))
  }, [])

  //  on input change
  useEffect(() => {
    if (cancelToken.current) {
      cancelToken.current.cancel('oops')
    }
    const t = generateCancelToken()
    cancelToken.current = t

    debouncedOnChangeValue(itemKey, t.token)
  }, [value])

  //  polling on sender registration
  useEffect(() => {
    if (
      !pollingError &&
      status === TRUSTED_SENDER_STATUS_NOT_CONFIRMED &&
      pollingIteration > 0
    ) {
      pollingTimer.current = setTimeout(
        statusPolling,
        POLLING_INTERVAL,
        itemKey,
        pollingCancelToken.current.token
      )
    }
  }, [status, pollingError, pollingIteration])

  //  cleanup
  useEffect(() => {
    return () => {
      clearTimeout(pollingTimer)
      if (pollingCancelToken.current) {
        pollingCancelToken.current.cancel('cancel polling on unmount')
      }
      if (cancelToken.current) {
        cancelToken.current.cancel('cancel on unmount')
      }
      debouncedOnChangeValue.cancel()
    }
  }, [])

  const i18n = {
    invalid: invalidRequired
      ? intl.formatMessage({ id: 'Load.TrustedEmailInput.EmptyMessage' })
      : invalidFormat
        ? intl.formatMessage({
            id: 'Load.TrustedEmailInput.WrongFormatMessage'
          })
        : undefined,
    unverified: intl.formatMessage({
      id: 'Load.TrustedEmailInput.NotConfirmed'
    }),
    verifyLink: intl.formatMessage({
      id: 'Load.TrustedEmailInput.RequestConfirmation'
    }),
    error: intl.formatMessage({
      id: 'Load.TrustedEmailInput.DeliverabilityCheckError'
    }),
    errorTooltip:
      status === TRUSTED_SENDER_STATUS_DELIVER_ERROR
        ? intl.formatMessage({ id: 'Load.Deliverability.Error' })
        : status === TRUSTED_SENDER_STATUS_DELIVER_ISP
          ? intl.formatMessage({ id: 'Load.Deliverability.Isp' })
          : undefined,
    warning: intl.formatMessage({
      id: 'Load.TrustedEmailInput.DeliverabilityCheckWarning'
    }),
    warningTooltip: intl.formatMessage({ id: 'Load.Deliverability.Warning' }),
    blocked: intl.formatMessage({ id: 'Load.TrustedEmailInput.Blocked' })
  }

  return {
    value,
    onChange: handleChange,
    onDismissWarningClick: handleDismissWarningClick,
    onVerifyClick: handleVerifyClick,
    variant,
    i18n
  }
}

export default useTrustedEmailSender
